import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiSend, FiUser, FiEyeOff, FiEye, FiAlertCircle, FiCopy, FiMapPin, FiMessageSquare, FiMap, FiPaperclip,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { EnderecoComMapa, ENDERECO_VAZIO, enderecoComoTexto, type EnderecoMapaValor } from '../../components/EnderecoComMapa';
import { AnexoUpload } from '../../components/AnexoUpload';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  ouvidoriaPortalService, TIPO_MANIFESTACAO_LABEL,
  type TipoManifestacao, type AssuntoOuvidoria,
} from '../../services/ouvidoria.service';
import { solicitacoesService } from '../../services/solicitacoes.service';

const EXT_ANEXOS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
const MAX_ANEXOS = 5;

type Modo = 'identificada' | 'sigilosa' | 'anonima';

const MODO_INFO: Record<Modo, { titulo: string; descricao: string; icone: typeof FiUser }> = {
  identificada: {
    titulo: 'Identificada',
    descricao: 'Seu nome ficará visível para o setor responsável. Você receberá retorno por e-mail.',
    icone: FiUser,
  },
  sigilosa: {
    titulo: 'Sigilosa',
    descricao: 'Sua identidade é preservada — apenas o Ouvidor poderá vê-la. Você recebe retorno.',
    icone: FiEyeOff,
  },
  anonima: {
    titulo: 'Anônima',
    descricao: 'Nenhum dado de identificação será enviado. Você não receberá retorno individualizado.',
    icone: FiEye,
  },
};

export function OuvidoriaPage() {
  const { session, cidadao } = useAuth();
  const navigate = useNavigate();

  const [modo, setModo] = useState<Modo>(session ? 'identificada' : 'anonima');
  const [tipo, setTipo] = useState<TipoManifestacao>('reclamacao');
  const [assuntoId, setAssuntoId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [loc, setLoc] = useState<EnderecoMapaValor>(ENDERECO_VAZIO);
  const [anexos, setAnexos] = useState<File[]>([]);

  const [assuntos, setAssuntos] = useState<AssuntoOuvidoria[]>([]);
  const [carregandoAssuntos, setCarregandoAssuntos] = useState(true);

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string; hash_publico: string } | null>(null);

  useEffect(() => {
    ouvidoriaPortalService.listarAssuntos(getEstabelecimentoAtualId())
      .then((a) => {
        setAssuntos(a);
        if (a.length > 0) setAssuntoId(a[0].id);
      })
      .finally(() => setCarregandoAssuntos(false));
  }, []);

  // Se nao logado, força anonima
  useEffect(() => {
    if (!session && modo !== 'anonima') setModo('anonima');
  }, [session, modo]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    if (titulo.trim().length < 3) { setErro('Informe um título.'); return; }
    if (descricao.trim().length < 10) { setErro('Detalhe sua manifestação (mínimo 10 caracteres).'); return; }
    if (!assuntoId) { setErro('Selecione um assunto.'); return; }

    setEnviando(true);
    try {
      const baseGeo = {
        endereco: enderecoComoTexto(loc) || undefined,
        cep: loc.cep.replace(/\D/g, '') || undefined,
        bairro: loc.bairro || undefined,
        latitude: loc.latitude ?? undefined,
        longitude: loc.longitude ?? undefined,
      };

      let r: { numero: string; hash_publico: string };

      if (modo === 'anonima') {
        r = await ouvidoriaPortalService.abrirAnonima({
          estabelecimento_id: getEstabelecimentoAtualId(),
          assunto_id: assuntoId,
          tipo,
          titulo: titulo.trim(),
          descricao: descricao.trim(),
          ...baseGeo,
        });
      } else {
        if (!cidadao) { setErro('Sessão expirada. Faça login novamente.'); setEnviando(false); return; }
        const doc = await ouvidoriaPortalService.abrirIdentificada({
          estabelecimento_id: getEstabelecimentoAtualId(),
          cidadao_id: cidadao.id,
          assunto_id: assuntoId,
          tipo,
          titulo: titulo.trim(),
          descricao: descricao.trim(),
          sigilosa: modo === 'sigilosa',
          ...baseGeo,
        });
        r = { numero: doc.numero, hash_publico: doc.hash_publico };
        // Anexos diversos (cidadão logado).
        for (const arquivo of anexos) {
          await solicitacoesService.uploadAnexo({
            documentoId: doc.id,
            estabelecimentoId: getEstabelecimentoAtualId(),
            cidadaoId: cidadao.id,
            cidadaoNome: cidadao.nome,
            arquivo,
          });
        }
        // E-mail de confirmação de abertura (best-effort).
        try { await supabase.functions.invoke('notificar-cidadao-protocolo', { body: { documento_id: doc.id, evento: 'criado' } }); } catch { /* não bloqueia */ }
      }

      setResultado(r);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) {
    return <PainelSucesso numero={resultado.numero} hash={resultado.hash_publico} modo={modo} onNovo={() => {
      setResultado(null); setTitulo(''); setDescricao(''); setLoc(ENDERECO_VAZIO);
    }} onVerSolicitacoes={() => navigate(session ? '/minhas-solicitacoes' : '/')} />;
  }

  return (
    <PortalPage
      titulo="Ouvidoria"
      subtitulo="Canal oficial para sugestões, reclamações, denúncias, elogios e pedidos de informação (Lei Federal 13.460/2017)."
      icone={<FiMessageSquare size={26} />}
      voltarHref={session ? '/' : '/login'}
      acoes={
        <Link
          to="/ouvidoria/mapa"
          className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
        >
          <FiMap size={15} /> Mapa público
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        {/* Modo de identificação */}
        <SectionCard
          titulo="Como você quer se identificar?"
          subtitulo="A escolha afeta quem vê seus dados e se você receberá retorno."
          icone={<FiUser size={18} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(Object.keys(MODO_INFO) as Modo[]).map((m) => {
                  const ativo = modo === m;
                  const desabilitado = !session && m !== 'anonima';
                  const { titulo: t, descricao: d, icone: Icone } = MODO_INFO[m];
                  return (
                    <button
                      key={m}
                      type="button"
                      disabled={desabilitado}
                      onClick={() => setModo(m)}
                      className={`text-left p-4 rounded border-2 transition ${
                        ativo
                          ? 'border-institutional bg-institutional/5'
                          : desabilitado
                            ? 'border-slate-200 opacity-50 cursor-not-allowed'
                            : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icone className="text-institutional mb-2" size={18} />
                      <div className="font-medium text-slate-900 text-sm">{t}</div>
                      <p className="text-xs text-slate-600 mt-1 leading-snug">{d}</p>
                      {desabilitado && (
                        <p className="text-[11px] text-amber-700 mt-2">Requer login.</p>
                      )}
                    </button>
                  );
                })}
          </div>
          {!session && (
            <div className="mt-3 text-xs text-slate-500">
              <Link to="/login" className="text-institutional underline">Entrar</Link>{' '}
              para usar manifestação identificada ou sigilosa e receber retorno.
            </div>
          )}
        </SectionCard>

        {/* Tipo + Assunto */}
        <SectionCard
          titulo="Sobre sua manifestação"
          subtitulo="Classifique e descreva o que você deseja registrar."
          icone={<FiSend size={18} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <Label obrigatorio>Tipo</Label>
                  <select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as TipoManifestacao)}
                    required
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
                  >
                    {(Object.keys(TIPO_MANIFESTACAO_LABEL) as TipoManifestacao[]).map((t) => (
                      <option key={t} value={t}>{TIPO_MANIFESTACAO_LABEL[t]}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label obrigatorio>Assunto</Label>
                  {carregandoAssuntos ? (
                    <div className="h-10 flex items-center"><Spinner tamanho="sm" /></div>
                  ) : assuntos.length === 0 ? (
                    <p className="text-xs text-amber-700 mt-1">
                      Nenhum assunto da Ouvidoria configurado pela prefeitura ainda.
                    </p>
                  ) : (
                    <select
                      value={assuntoId}
                      onChange={(e) => setAssuntoId(e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
                    >
                      {assuntos.map((a) => (
                        <option key={a.id} value={a.id}>{a.nome}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="titulo" obrigatorio>Título</Label>
                  <Input
                    id="titulo"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Resuma sua manifestação em uma frase"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="descricao" obrigatorio>Descrição</Label>
                  <textarea
                    id="descricao"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    rows={6}
                    required
                    placeholder="Descreva com o máximo de detalhes possíveis: o que aconteceu, onde, quando..."
                    className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
                  />
                </div>
              </div>
        </SectionCard>

        {/* Localização (opcional) */}
        <SectionCard
          titulo="Localização (opcional)"
          subtitulo="Informe o CEP/endereço (preenche e marca no mapa) ou escolha direto no mapa."
          icone={<FiMapPin size={18} />}
        >
          <EnderecoComMapa valor={loc} onChange={setLoc} />
        </SectionCard>

        {modo !== 'anonima' && (
          <SectionCard
            titulo="Anexos (opcional)"
            subtitulo={`Documentos, fotos ou comprovantes. Até ${MAX_ANEXOS} arquivos.`}
            icone={<FiPaperclip size={18} />}
          >
            <AnexoUpload
              arquivos={anexos}
              onChange={setAnexos}
              extensoesPermitidas={EXT_ANEXOS}
              tamanhoMaxMB={10}
              maxArquivos={MAX_ANEXOS}
              legenda={`Até ${MAX_ANEXOS} arquivos · 10 MB cada · ${EXT_ANEXOS.join(', ').toUpperCase()}`}
            />
          </SectionCard>
        )}

        {modo === 'sigilosa' && (
          <Alert variante="info">
            Denúncias são levadas a sério. O Ouvidor é o único servidor com acesso à sua identidade
            em modo sigiloso.
          </Alert>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(session ? '/' : '/login')}>
            Cancelar
          </Button>
          <Button type="submit" loading={enviando} disabled={assuntos.length === 0}>
            <FiSend size={14} /> Enviar manifestação
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}

function PainelSucesso({
  numero, hash, modo, onNovo, onVerSolicitacoes,
}: {
  numero: string;
  hash: string;
  modo: Modo;
  onNovo: () => void;
  onVerSolicitacoes: () => void;
}) {
  const linkAcompanhar = `${window.location.origin}/protocolo/${numero}/${hash}`;
  const [copiado, setCopiado] = useState(false);

  function copiar() {
    navigator.clipboard.writeText(linkAcompanhar);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <PainelResultado
      titulo="Manifestação registrada"
      descricao={<>Seu protocolo é <strong className="text-slate-900">{numero}</strong></>}
      acoes={
        <>
          <Button variant="outline" onClick={onNovo}>
            <FiAlertCircle size={14} /> Nova manifestação
          </Button>
          <Button onClick={onVerSolicitacoes}>Ver minhas solicitações</Button>
        </>
      }
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Label>Link de acompanhamento</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={linkAcompanhar} readOnly className="text-xs flex-1" />
          <Button type="button" variant="outline" onClick={copiar}>
            <FiCopy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {modo === 'anonima'
            ? 'Guarde este link — é a única forma de acompanhar uma manifestação anônima.'
            : 'Você também pode acompanhar pela área "Minhas solicitações" no portal.'}
        </p>
      </div>

      {modo !== 'anonima' && (
        <Alert variante="info" className="mt-4">
          Você receberá notificações por e-mail a cada movimentação importante.
        </Alert>
      )}
    </PainelResultado>
  );
}
